import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  BookOpen, 
  Clock, 
  ArrowRight, 
  Wrench,
  TrendingUp,
  Globe,
  ShieldCheck,
  FileText,
  Layers,
  Cog,
  Home,
  Target,
  Search,
  Gauge,
  HardHat,
  Droplets,
  Settings,
  DollarSign,
  Filter
} from 'lucide-react';

// Définition des catégories de guides
export const guideCategories = [
  { 
    id: 'materiel-foreuses', 
    name: 'Matériel & foreuses', 
    icon: Wrench, 
    color: 'bg-blue-500',
    description: 'Guides pour choisir, acheter et utiliser votre matériel de forage géotechnique.'
  },
  { 
    id: 'instrumentation-mesure', 
    name: 'Instrumentation & mesure', 
    icon: TrendingUp, 
    color: 'bg-indigo-500',
    description: 'Tout sur les essais in situ, les capteurs et l\'instrumentation de chantier.'
  },
  { 
    id: 'etudes-fondations', 
    name: 'Études & fondations', 
    icon: Layers, 
    color: 'bg-purple-500',
    description: 'Études géotechniques, fondations spéciales, micropieux et techniques de sol.'
  },
  { 
    id: 'forage-eau', 
    name: 'Forage d\'eau', 
    icon: Droplets, 
    color: 'bg-sky-500',
    description: 'Guides pour les projets de forage d\'eau : prix, démarches, réglementation.'
  },
  { 
    id: 'vendre-estimer', 
    name: 'Vendre & estimer', 
    icon: DollarSign, 
    color: 'bg-amber-500',
    description: 'Estimer la valeur de votre matériel, préparer une vente, trouver un acheteur.'
  },
];

const GuidesIndex = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Liste des guides publiés avec catégories
  const publishedGuides = [
    {
      id: 'foreuse',
      slug: '/guides/comment-choisir-foreuse-geotechnique',
      title: 'Comment choisir une foreuse géotechnique ?',
      subtitle: 'Guide complet 2026',
      description: 'Tout savoir pour choisir la foreuse idéale : 10 critères essentiels, 4 familles de machines, comparatif des marques, prix neuf et occasion.',
      readTime: '12 min',
      icon: Wrench,
      color: 'bg-blue-500',
      tags: ['Foreuse', 'Achat', 'Location', 'Occasion'],
      category: 'materiel-foreuses',
      published: true,
    },
    {
      id: 'etude-geotechnique',
      slug: '/guides/comment-choisir-etude-geotechnique',
      title: 'Comment choisir son étude géotechnique ?',
      subtitle: 'Missions G1 à G5 expliquées',
      description: 'Quelle étude géotechnique choisir pour votre projet ? Guide complet des missions G1, G2, G3, G4, G5 selon la norme NF P 94-500.',
      readTime: '10 min',
      icon: FileText,
      color: 'bg-green-500',
      tags: ['Étude de sol', 'G1', 'G2', 'NF P 94-500'],
      category: 'etudes-fondations',
      published: true,
    },
    {
      id: 'fondations-speciales',
      slug: '/guides/comment-choisir-fondations-speciales',
      title: 'Comment choisir ses fondations spéciales ?',
      subtitle: 'Pieux, micropieux, parois moulées',
      description: 'Pieux, micropieux, parois moulées, colonnes ballastées, jet-grouting : quelle fondation spéciale choisir selon votre sol et votre projet ?',
      readTime: '12 min',
      icon: ShieldCheck,
      color: 'bg-purple-500',
      tags: ['Pieux', 'Micropieux', 'Paroi moulée', 'Jet-grouting'],
      category: 'etudes-fondations',
      published: true,
    },
    {
      id: 'paroi-moulee',
      slug: '/guides/paroi-moulee-technique-realisation',
      title: 'Paroi moulée : technique et réalisation',
      subtitle: 'Benne, hydrofraise, étapes',
      description: 'Tout sur la paroi moulée : étapes de réalisation, benne et hydrofraise, boue bentonite, paramètres de conception et applications.',
      readTime: '14 min',
      icon: Layers,
      color: 'bg-cyan-500',
      tags: ['Paroi moulée', 'Soutènement', 'Benne', 'Hydrofraise'],
      category: 'etudes-fondations',
      published: true,
    },
    {
      id: 'materiel-forage',
      slug: '/guides/materiel-de-forage',
      title: 'Matériel de forage : le guide complet',
      subtitle: 'Machines, outils, instrumentation',
      description: 'Foreuse, tarière, carottier, trépan, tubage, enregistreur de paramètres : le guide complet du matériel de forage géotechnique.',
      readTime: '15 min',
      icon: Cog,
      color: 'bg-orange-500',
      tags: ['Tarière', 'Carottier', 'Instrumentation', 'Outils'],
      category: 'materiel-foreuses',
      published: true,
    },
    {
      id: 'fissures-maison',
      slug: '/guides/fissures-maison-quand-s-inquieter',
      title: 'Fissures maison : quand s\'inquiéter ?',
      subtitle: 'Types, causes, solutions',
      description: 'Fissure sur un mur de votre maison : est-ce grave ? Types de fissures, signaux d\'alerte, causes (sécheresse RGA), solutions et assurance.',
      readTime: '12 min',
      icon: Home,
      color: 'bg-red-500',
      tags: ['Fissures', 'Maison', 'Sécheresse', 'Diagnostic'],
      category: 'etudes-fondations',
      published: true,
    },
    {
      id: 'enregistreur-parametres',
      slug: '/guides/enregistreur-parametres-forage',
      title: 'Enregistreur de paramètres de forage',
      subtitle: 'DAC, contrôle et traçabilité',
      description: 'Tout sur l\'enregistreur de paramètres de forage (EPF) : rôle, paramètres mesurés, contrôle qualité des pieux, conformité NF EN 1536.',
      readTime: '8 min',
      icon: TrendingUp,
      color: 'bg-indigo-500',
      tags: ['DAC', 'Contrôle qualité', 'NF EN 1536', 'Traçabilité'],
      category: 'instrumentation-mesure',
      published: true,
    },
    {
      id: 'micropieux',
      slug: '/guides/micropieux',
      title: 'Micropieux : guide complet',
      subtitle: 'Types, mise en œuvre et reprise en sous-œuvre',
      description: 'Tout sur les micropieux : les 4 types (I à IV), mise en œuvre, reprise en sous-œuvre d\'une maison, normes NF EN 14199 et DTU 13.2.',
      readTime: '10 min',
      icon: Target,
      color: 'bg-cyan-500',
      tags: ['Micropieux', 'Reprise sous-œuvre', 'NF EN 14199', 'Types I-IV'],
      category: 'etudes-fondations',
      published: true,
    },
    {
      id: 'essai-pressiometrique',
      slug: '/guides/essai-pressiometrique-menard',
      title: 'Essai pressiométrique Ménard : EM, pl* et interprétation',
      subtitle: 'Norme NF EN ISO 22476-4',
      description: 'Comprendre l\'essai pressiométrique Ménard : principe, norme NF EN ISO 22476-4, paramètres EM, pl* et pf, lecture de la courbe et dimensionnement des fondations.',
      readTime: '10 min',
      icon: TrendingUp,
      color: 'bg-indigo-500',
      tags: ['Pressiomètre', 'EM', 'pl*', 'NF EN ISO 22476-4'],
      category: 'instrumentation-mesure',
      published: true,
    },
    {
      id: 'injection',
      slug: '/guides/injection-coulis-traitement-de-sol',
      title: 'Injection en géotechnique : coulis et traitement de sol',
      subtitle: 'Mécanismes, produits, résine expansive',
      description: 'Injection de coulis de ciment, comblement, traitement de sol, résine expansive : mécanismes, produits, étapes de chantier et contrôle selon la norme NF EN 12715.',
      readTime: '12 min',
      icon: Layers,
      color: 'bg-teal-500',
      tags: ['Injection', 'Coulis', 'Résine expansive', 'NF EN 12715'],
      category: 'etudes-fondations',
      published: true,
    },
    {
      id: 'foreuse-occasion',
      slug: '/guides/foreuse-occasion-micropieux',
      title: 'Foreuse d\'occasion pour micropieux : guide d\'achat',
      subtitle: 'Critères, documents, pièges à éviter',
      description: 'Acheter une foreuse d\'occasion pour micropieux : critères techniques, points d\'usure, heures de forage, documents (CE, VGP) et pièges à éviter.',
      readTime: '12 min',
      icon: Search,
      color: 'bg-amber-500',
      tags: ['Foreuse occasion', 'Micropieux', 'VGP', 'Guide achat'],
      category: 'materiel-foreuses',
      published: true,
    },
    {
      id: 'pressiometre-choix',
      slug: '/guides/choisir-pressiometre-manuel-automatique',
      title: 'Choisir son pressiomètre : manuel ou automatique ?',
      subtitle: 'Neuf, occasion ou location',
      description: 'Pressiomètre manuel ou auto-contrôlé, neuf, occasion ou location : critères de choix, exigences des marchés, étalonnage et points de vigilance.',
      readTime: '10 min',
      icon: Gauge,
      color: 'bg-violet-500',
      tags: ['Pressiomètre', 'Manuel', 'Automatique', 'Étalonnage'],
      category: 'instrumentation-mesure',
      published: true,
    },
    {
      id: 'metier-foreur',
      slug: '/guides/metier-foreur-sondeur',
      title: 'Foreur ou sondeur : métier, compétences et formations',
      subtitle: 'CACES, AIPR, évolution de carrière',
      description: 'Le métier de foreur et de sondeur en géotechnique : missions, maîtrise des appareils de mesure, certifications CACES et AIPR, formations et évolution de carrière.',
      readTime: '12 min',
      icon: HardHat,
      color: 'bg-emerald-500',
      tags: ['Métier', 'Foreur', 'Sondeur', 'CACES', 'Formation'],
      category: 'materiel-foreuses',
      published: true,
    },
    {
      id: 'prix-forage-eau',
      slug: '/guides/prix-forage-eau',
      title: 'Prix d\'un forage d\'eau en 2026 : combien ça coûte vraiment ?',
      subtitle: 'Budget, démarches, DUPLOS',
      description: 'Prix d\'un forage d\'eau en 2026 : coût au mètre, budget complet, équipements, démarches obligatoires (déclaration mairie, DUPLOS). Le guide pour estimer votre projet.',
      readTime: '8 min',
      icon: Droplets,
      color: 'bg-sky-500',
      tags: ['Forage eau', 'Prix', 'DUPLOS', 'Particuliers'],
      category: 'forage-eau',
      published: true,
    },
    {
      id: 'etalonnage-capteurs',
      slug: '/guides/etalonnage-capteurs-forage',
      title: 'Étalonnage des capteurs de forage : obligations, normes et bonnes pratiques',
      subtitle: 'ISO 17025, traçabilité, certificat',
      description: 'Étalonnage des capteurs de forage et d\'instrumentation géotechnique : obligations, norme ISO 17025, traçabilité métrologique, fréquence et certificat. Le guide complet.',
      readTime: '10 min',
      icon: Settings,
      color: 'bg-slate-600',
      tags: ['Étalonnage', 'ISO 17025', 'Capteurs', 'Métrologie'],
      category: 'instrumentation-mesure',
      published: true,
    },
  ];

  // Guides à venir (teasing)
  const upcomingGuides = [
    {
      id: 'acheter-louer',
      title: 'Acheter ou louer une foreuse : 7 critères pour décider',
      description: 'Le guide pour faire le bon choix selon votre activité, votre budget et vos contraintes.',
      icon: TrendingUp,
      color: 'bg-green-500',
    },
    {
      id: 'import-maghreb',
      title: 'Importer du matériel BTP de France vers le Maghreb',
      description: 'Douanes, normes, transport : le guide pratique pour importer sereinement.',
      icon: Globe,
      color: 'bg-orange-500',
    },
    {
      id: 'occasion-verification',
      title: 'Foreuse d\'occasion : 10 points à vérifier avant d\'acheter',
      description: 'Checklist complète pour éviter les mauvaises surprises.',
      icon: ShieldCheck,
      color: 'bg-purple-500',
    },
  ];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Guides Géotechniques | GEOTS Consulting",
    "description": "Guides pratiques pour les professionnels de la géotechnique : choisir son équipement, acheter ou louer, importer du matériel.",
    "url": "https://geots.consulting/guides",
    "publisher": {
      "@type": "Organization",
      "name": "GEOTS Consulting"
    }
  };

  return (
    <>
      <Helmet>
        <title>Guides Géotechniques | Conseils d&apos;experts | GEOTS Consulting</title>
        <meta name="description" content="Guides pratiques pour les professionnels de la géotechnique : choisir son équipement, acheter ou louer, importer du matériel. Conseils d'experts." />
        <link rel="canonical" href="https://geots.consulting/guides" />
        <meta property="og:title" content="Guides Géotechniques | GEOTS Consulting" />
        <meta property="og:description" content="Guides pratiques pour les professionnels de la géotechnique." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://geots.consulting/guides" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <header className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-[#635BFF] p-3 rounded-xl">
                  <BookOpen className="w-8 h-8" />
                </div>
                <span className="text-[#00D4FF] font-semibold">Centre de ressources</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Guides & Ressources
                <span className="text-[#00D4FF]"> Géotechniques</span>
              </h1>
              <p className="text-xl text-gray-300">
                Des guides pratiques rédigés par des professionnels du métier pour vous aider 
                à faire les bons choix : équipement, achat, location, import...
              </p>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-12">
          {/* Category Filter Bar */}
          <section className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-600">Filtrer par catégorie :</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === null
                    ? 'bg-[#635BFF] text-white shadow-md'
                    : 'bg-white text-gray-600 border hover:border-[#635BFF] hover:text-[#635BFF]'
                }`}
              >
                Tous les guides ({publishedGuides.length})
              </button>
              {guideCategories.map((cat) => {
                const count = publishedGuides.filter(g => g.category === cat.id).length;
                if (count === 0) return null; // Don't show empty categories
                const CategoryIcon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                      selectedCategory === cat.id
                        ? `${cat.color} text-white shadow-md`
                        : 'bg-white text-gray-600 border hover:border-[#635BFF] hover:text-[#635BFF]'
                    }`}
                  >
                    <CategoryIcon className="w-4 h-4" />
                    {cat.name} ({count})
                  </button>
                );
              })}
            </div>
            {/* Link to category page if filter active */}
            {selectedCategory && (
              <div className="mt-4">
                <Link 
                  to={`/guides/categorie/${selectedCategory}`}
                  className="text-sm text-[#635BFF] hover:underline flex items-center gap-1"
                >
                  Voir la page dédiée « {guideCategories.find(c => c.id === selectedCategory)?.name} »
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </section>

          {/* Published Guides */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <FileText className="w-6 h-6 text-[#635BFF]" />
              <h2 className="text-2xl font-bold text-[#0A2540]">
                {selectedCategory 
                  ? guideCategories.find(c => c.id === selectedCategory)?.name 
                  : 'Guides publiés'}
              </h2>
              <span className="bg-[#635BFF] text-white text-xs font-bold px-2 py-1 rounded-full">
                {selectedCategory 
                  ? publishedGuides.filter(g => g.category === selectedCategory).length
                  : publishedGuides.length}
              </span>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {publishedGuides
                .filter(guide => !selectedCategory || guide.category === selectedCategory)
                .map((guide) => (
                <Link
                  key={guide.id}
                  to={guide.slug}
                  className="group bg-white rounded-2xl border hover:shadow-xl transition-all duration-300 overflow-hidden"
                >
                  {/* Card Header */}
                  <div className={`${guide.color} p-6 text-white relative overflow-hidden`}>
                    <div className="absolute top-0 right-0 opacity-10">
                      <guide.icon className="w-32 h-32 -mr-8 -mt-8" />
                    </div>
                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="bg-white/20 text-white text-xs font-bold px-2 py-1 rounded">
                          GUIDE COMPLET
                        </span>
                        <span className="flex items-center gap-1 text-sm text-white/80">
                          <Clock className="w-4 h-4" />
                          {guide.readTime}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold mb-1">{guide.title}</h3>
                      <p className="text-white/80 text-sm">{guide.subtitle}</p>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-6">
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {guide.description}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {guide.tags.map((tag, i) => (
                        <span key={i} className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center text-[#635BFF] font-semibold group-hover:gap-3 transition-all">
                      Lire le guide
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Upcoming Guides */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <Clock className="w-6 h-6 text-gray-400" />
              <h2 className="text-2xl font-bold text-[#0A2540]">Prochainement</h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingGuides.map((guide) => (
                <div
                  key={guide.id}
                  className="bg-white rounded-2xl border p-6 opacity-75 relative overflow-hidden"
                >
                  <div className="absolute top-4 right-4">
                    <span className="bg-gray-100 text-gray-500 text-xs font-bold px-2 py-1 rounded">
                      BIENTÔT
                    </span>
                  </div>
                  <div className={`${guide.color} w-12 h-12 rounded-xl flex items-center justify-center mb-4 opacity-50`}>
                    <guide.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-[#0A2540] mb-2">{guide.title}</h3>
                  <p className="text-gray-500 text-sm">{guide.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA Newsletter */}
          <section className="bg-gradient-to-br from-[#635BFF] to-[#8B5CF6] rounded-2xl p-8 md:p-12 text-white text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Recevez nos prochains guides
            </h2>
            <p className="text-white/80 mb-6 max-w-2xl mx-auto">
              Inscrivez-vous à notre newsletter pour être notifié dès la publication 
              d&apos;un nouveau guide et recevoir des conseils exclusifs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <input
                type="email"
                placeholder="Votre email professionnel"
                className="flex-1 px-4 py-3 rounded-lg text-[#0A2540] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white"
              />
              <button className="bg-white text-[#635BFF] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                S&apos;abonner
              </button>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default GuidesIndex;
